import React, { useState, useContext, useRef, useEffect } from "react";
import "./Discipline.css";
import { UserContext } from "../../../contexts/CurrentUserContext";
import MyDiscipline from "./MyDiscipline/MyDiscipline";
import DepartmentDiscipline from "./DepartmentDiscipline/DepartmentDiscipline";
import EmployeeDiscipline from "./EmployeeDiscipline/EmployeeDiscipline";
import EmployeeTimesheet from "./EmployeeTimesheet/EmployeeTimesheet";
import { getAPIURL } from "../../../api/config";

const Discipline = () => {
  const { userData } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("my");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Проверка доступа к вкладке "Рейтинг отделов" - доступна всем
  const hasDepartmentAccess = true;

  // Проверка доступа к вкладке "Дисциплина по отделу" - только для РО, НО, АДМС, ИК + пользователь с ID 63
  const hasEmployeeAccess =
    userData &&
    (userData.id === 63 ||
      (userData.access_levels &&
        userData.access_levels.some(
          (level) =>
            level.name === "РО" ||
            level.name === "НО" ||
            level.name === "АДМС" ||
            level.name === "ИК"
        )));

  // Проверка для кнопки загрузки - только АДМС
  const hasUploadAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some((level) => level.name === "АДМС");

  // Проверка доступа к вкладке "Дисциплина по сотруднику" - для АДМС, РО, РГ, НО, ИК
  const hasEmployeeTimesheetAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some(
      (level) =>
        level.name === "АДМС" ||
        level.name === "РО" ||
        level.name === "РГ" ||
        level.name === "НО" ||
        level.name === "ИК"
    );

  // Проверяем доступность вкладки и переключаемся на доступную, если нужно
  useEffect(() => {
    if (activeTab === "departments" && !hasDepartmentAccess) {
      setActiveTab("my");
    }
    if (activeTab === "employees" && !hasEmployeeAccess) {
      setActiveTab("my");
    }
    if (activeTab === "employee-timesheet" && !hasEmployeeTimesheetAccess) {
      setActiveTab("my");
    }
  }, [
    activeTab,
    hasDepartmentAccess,
    hasEmployeeAccess,
    hasEmployeeTimesheetAccess,
  ]);

  // Обработчик выбора файла
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // MIME на macOS/Safari иногда пустой — опираемся на расширение
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/octet-stream",
    ];
    const hasValidExtension = /\.(xlsx|xls)$/i.test(file.name);
    if (!hasValidExtension && !validTypes.includes(file.type)) {
      alert("Пожалуйста, выберите Excel файл (.xls или .xlsx)");
      event.target.value = null;
      return;
    }

    await uploadFile(file);
    // Очистка input для возможности повторной загрузки того же файла
    event.target.value = null;
  };

  // Отправка файла на сервер
  const uploadFile = async (file) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Auth в проекте через cookies (credentials: "include"),
      // а не через localStorage accessToken
      const response = await fetch(
        `${getAPIURL()}discipline/user_attendances/add`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const message = Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
          : detail || errorData.message || "Ошибка при загрузке файла";
        throw new Error(message);
      }

      const data = await response.json();
      alert(data.message || "Файл успешно загружен!");

      // Перезагрузка данных в активной вкладке
      window.location.reload();
    } catch (error) {
      console.error("Ошибка загрузки файла:", error);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Обработчик клика на кнопку загрузки
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="discipline-page">
      <div className="discipline-header">
        <h1 className="discipline-title">Дисциплина</h1>
      </div>

      {/* Вкладки и кнопка загрузки */}
      <div className="discipline-tabs-container">
        <div className="discipline-tabs">
          <button
            className={`tab-button ${activeTab === "my" ? "active" : ""}`}
            onClick={() => setActiveTab("my")}
          >
            Моя дисциплина
          </button>
          {hasDepartmentAccess && (
            <button
              className={`tab-button ${
                activeTab === "departments" ? "active" : ""
              }`}
              onClick={() => setActiveTab("departments")}
            >
              Рейтинг отделов
            </button>
          )}
          {hasEmployeeAccess && (
            <button
              className={`tab-button ${
                activeTab === "employees" ? "active" : ""
              }`}
              onClick={() => setActiveTab("employees")}
            >
              Дисциплина по отделу
            </button>
          )}
          {hasEmployeeTimesheetAccess && (
            <button
              className={`tab-button ${
                activeTab === "employee-timesheet" ? "active" : ""
              }`}
              onClick={() => setActiveTab("employee-timesheet")}
            >
              Дисциплина по сотруднику
            </button>
          )}
        </div>

        {/* Кнопка загрузки Excel - только для АДМС */}
        {hasUploadAccess && (
          <div className="discipline-upload-section">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
            />
            <button
              className="upload-excel-button"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              {isUploading ? "Загрузка..." : "Загрузить Excel"}
            </button>
          </div>
        )}
      </div>

      {/* Контент вкладок */}
      <div className="discipline-content">
        {activeTab === "my" && <MyDiscipline />}
        {activeTab === "departments" && hasDepartmentAccess && (
          <DepartmentDiscipline />
        )}
        {activeTab === "employees" && hasEmployeeAccess && (
          <EmployeeDiscipline />
        )}
        {activeTab === "employee-timesheet" && hasEmployeeTimesheetAccess && (
          <EmployeeTimesheet />
        )}
      </div>
    </div>
  );
};

export default Discipline;
